import React, { useMemo } from 'react';
import { SearchDropdownBase, type FieldBehavior } from './SearchDropdownBase';

export type TextSearchDropdownProps = {
  label: string;

  value?: string | null | undefined;
  defaultValue?: string;
  onChange?: (value: string) => void;

  /** Character cap (default 50) */
  maxChars?: number;

  searchPlaceholder?: string;

  className?: string;
  disabled?: boolean;

  onOpenChange?: (open: boolean) => void;

  controlWidth?: number | string;
  controlMaxWidth?: number | string;
  controlHeight?: number | string;
  iconSize?: number | string;

  panelMinWidth?: number | string;
  panelMaxWidth?: number | string;
  panelMaxHeight?: number;

  /** Defaults to single-line text input */
  rows?: number;        // ignored in single-line
  autoGrow?: never;     // not used in single-line

  /** Close on Enter (single-line; default true) */
  closeOnEnter?: boolean;

  /** Clear button (to the right of the input) */
  showToolbarClear?: boolean;
  clearLabel?: string;

  /** Called when user presses Enter */
  onSearch?: (text: string) => void;
};

export function TextSearchDropdown({
  label,
  value,
  defaultValue,
  onChange,
  maxChars = 50,
  searchPlaceholder,
  className,
  disabled,
  onOpenChange,
  controlWidth,
  controlMaxWidth,
  controlHeight = 36,
  iconSize,
  panelMinWidth = 360,
  panelMaxWidth = 560,
  panelMaxHeight = 320,
  rows,
  autoGrow,
  closeOnEnter = true,
  showToolbarClear = true,
  clearLabel,
  onSearch,
}: TextSearchDropdownProps) {
  const behavior: FieldBehavior = useMemo(() => {
    const sanitize = (raw: string) => (raw ?? '');
    const validate = (_raw: string) => ({ error: null });
    const onKeyDown = (_e: React.KeyboardEvent<any>) => { /* no-op */ };

    return {
      fieldKind: 'input',
      inputMode: undefined,
      maxLengthAttr: typeof maxChars === 'number' ? maxChars : 50,
      sanitizeOnChange: sanitize,
      onKeyDown,
      finalizeOnBlur: undefined,
      normalizeOnClose: undefined,
      validate,
      caretToEndOnOpen: false,
    };
  }, [maxChars]);

  return (
    <SearchDropdownBase
      label={label}
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      searchPlaceholder={searchPlaceholder}
      className={className}
      disabled={disabled}
      onOpenChange={onOpenChange}
      controlWidth={controlWidth}
      controlMaxWidth={controlMaxWidth}
      controlHeight={controlHeight}
      iconSize={iconSize}
      panelMinWidth={panelMinWidth}
      panelMaxWidth={panelMaxWidth}
      panelMaxHeight={panelMaxHeight}
      rows={rows}
      autoGrow={false as never}
      closeOnEnter={closeOnEnter}
      showToolbarClear={showToolbarClear}
      clearLabel={clearLabel}
      behavior={behavior}
      onSearch={onSearch}
    />
  );
}
